import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Toaster } from "./components/ui/sonner";
import ErrorBoundary from "./components/ErrorBoundary";
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import HostDashboard from "./pages/HostDashboard";
import AdminPanel from "./pages/AdminPanel";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import EventPage from "./pages/EventPage";
import GuestEntry from "./pages/GuestEntry";
import GuestFeed from "./pages/GuestFeed";
import AlbumsPage from "./pages/AlbumsPage";
import TVSlideshow from "./pages/TVSlideshow";
import TVPairing from "./pages/TVPairing";
import UnlockPage from "./pages/UnlockPage";
import VideoCustomizePage from "./pages/VideoCustomizePage";
import { Loader2 } from "lucide-react";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFFBF7] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#FF6B6B]" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFFBF7] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#FF6B6B]" />
      </div>
    );
  }
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            {/* Public */}
            <Route path="/" element={<PublicRoute><LandingPage /></PublicRoute>} />
            <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
            <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />

            {/* Host (Protected) */}
            <Route path="/dashboard" element={<ProtectedRoute><HostDashboard /></ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute><AdminPanel /></ProtectedRoute>} />
            <Route path="/event/:eventId" element={<ProtectedRoute><EventPage /></ProtectedRoute>} />

            {/* Guest (Public) */}
            <Route path="/join/:eventId" element={<GuestEntry />} />
            <Route path="/feed/:eventId" element={<ErrorBoundary><GuestFeed /></ErrorBoundary>} />
            <Route path="/albums/:eventId" element={<AlbumsPage />} />

            {/* TV */}
            <Route path="/tv" element={<TVPairing />} />
            <Route path="/tv/:eventId" element={<TVSlideshow />} />

            {/* Payment */}
            <Route path="/unlock/:eventId" element={<UnlockPage />} />
            <Route path="/video/:eventId" element={<VideoCustomizePage />} />

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster position="top-center" richColors />
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
